import { useContext, useEffect, useState, useRef } from "react";
import { AuthContext, UserAuthStatus } from "../context/authContext";
import { useLoading } from "../context/loadingContext";
import { encode } from "base-64";

const getSsoParams = () => {
  // URL format: domain.com/#verificationCode=CODE&sessionId=ID&returnUrl=URL
  const hash = window.location.hash?.substring(1); // remove leading '#'
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const verificationCode = params.get("verificationCode");
  const sessionId = params.get("sessionId");
  const returnUrl = params.get("returnUrl") || null;
  return verificationCode && sessionId
    ? { verificationCode, sessionId, returnUrl }
    : null;
};

export function useSsoLogin() {
  const { authStatus, authenticateUser } = useContext(AuthContext);
  const { setLoading } = useLoading();
  const [ssoProcessing, setSsoProcessing] = useState(() => !!getSsoParams());
  const processedRef = useRef(false);

  useEffect(() => {
    const ssoParams = getSsoParams();
    if (!ssoParams || processedRef.current) return;
    if (authStatus === UserAuthStatus.Loading) return;

    processedRef.current = true;

    // Already signed in — redirect immediately
    if (authStatus === UserAuthStatus.SignedIn) {
      if (ssoParams.returnUrl) {
        window.location.href = ssoParams.returnUrl;
      } else {
        window.history.replaceState({}, document.title, "/");
        setSsoProcessing(false);
      }
      return;
    }

    // Not signed in — call SsoLoginComplete directly
    const doSsoLogin = async () => {
      try {
        setLoading(true);
        const baseHeaders = {
          Authorization:
            "Basic " +
            encode(
              process.env.REACT_APP_CMS_USERNAME +
                ":" +
                process.env.REACT_APP_CMS_PASSWORD
            ),
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers":
            "Origin, X-Requested-With, Content-Type, Accept",
        };

        const completeRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/user/sso-login-complete`,
          {
            method: "POST",
            headers: baseHeaders,
            body: JSON.stringify({
              VerificationCode: ssoParams.verificationCode,
              SessionCode: ssoParams.sessionId,
            }),
          }
        );

        if (!completeRes.ok) throw new Error(`SSO complete HTTP ${completeRes.status}`);

        const data = await completeRes.json();
        if (!data.success) throw new Error(data.errorMessage || "SSO failed");

        await authenticateUser(data.accessToken, data.idToken, data.refreshToken);

        if (ssoParams.returnUrl) {
          window.location.href = ssoParams.returnUrl;
          return; // keep loading spinner while redirecting
        } else {
          window.history.replaceState({}, document.title, "/");
          setSsoProcessing(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("SSO login failed:", err);
        setSsoProcessing(false);
        setLoading(false);
        // Clean query params on failure so normal login flow takes over
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    doSsoLogin();
  }, [authStatus, authenticateUser]);

  return { ssoProcessing };
}
